/**
 * Main entry point for the Tucuvi Intelligence frontend.
 *
 * This module runs as <script type="module"> which is deferred by the browser,
 * executing after HTML parsing but BEFORE DOMContentLoaded. This means anything
 * we put on `window` here is available to the legacy app.js callback.
 *
 * Migration strategy:
 * - As functions are extracted from app.js into src/ modules, they are imported
 *   here and exposed on `window` so the remaining app.js code can still call them.
 * - Once all views are migrated, app.js is deleted and window assignments are
 *   replaced with direct module imports.
 *
 * The shape of every seam published here is declared once in globals.d.ts.
 */

// --- Utilities ---
import {
    getEl, escapeHtml,
    pushModalTrigger, popModalTrigger, setupFocusTrap,
    infoTip, _showTip, _hideTip, initTooltipListeners,
    debounce
} from './utils/dom.js';

import { cssVar, hexAlpha, formatKey, formatDelta, formatDeltaNeutral } from './utils/formatting.js';
import { showToast, showConfirmModal } from './utils/notifications.js';
import {
    DEFAULT_TRANSCRIPTION_PROMPT, DEFAULT_TRANSCRIPTION_PROMPT_ES,
    getDefaultTranscriptionPrompt, getLocalizedTranscriptionPrompt,
    ensureTranscriptionPromptI18nDefaults
} from './utils/i18n.js';

// --- Window globals (needed for legacy app.js during migration) ---
// These assignments make extracted functions available to the app.js closure,
// which resolves bare variable names through the scope chain to window.

// DOM utilities
window.getEl = getEl;
window.escapeHtml = escapeHtml;
window.pushModalTrigger = pushModalTrigger;
window.popModalTrigger = popModalTrigger;
window.setupFocusTrap = setupFocusTrap;
window.infoTip = infoTip;
window._showTip = _showTip;
window._hideTip = _hideTip;
window.debounce = debounce;

// Formatting
window.cssVar = cssVar;
window.hexAlpha = hexAlpha;
window.formatKey = formatKey;
window.formatDelta = formatDelta;
window.formatDeltaNeutral = formatDeltaNeutral;

// Notifications
window.showToast = showToast;
window.showConfirmModal = showConfirmModal;

// i18n
window.DEFAULT_TRANSCRIPTION_PROMPT = DEFAULT_TRANSCRIPTION_PROMPT;
window.DEFAULT_TRANSCRIPTION_PROMPT_ES = DEFAULT_TRANSCRIPTION_PROMPT_ES;
window.getDefaultTranscriptionPrompt = getDefaultTranscriptionPrompt;
window.getLocalizedTranscriptionPrompt = getLocalizedTranscriptionPrompt;
window.ensureTranscriptionPromptI18nDefaults = ensureTranscriptionPromptI18nDefaults;

// --- Initialize tooltip listeners ---
// This must run at module load time (before DOMContentLoaded) so that
// tooltips work for any content rendered by app.js.
initTooltipListeners();

// --- State ---
import {
    state, onChange, set, FILTER_SESSION_KEY, AVAILABLE_MODELS, MODEL_CAPABILITIES,
    CAPABILITY_LABELS, EXPORT_VERSION
} from './state.js';

// State + constants on window for legacy app.js
window.state = state;
window.stateOnChange = onChange;
window.stateSet = set;
window.FILTER_SESSION_KEY = FILTER_SESSION_KEY;
window.AVAILABLE_MODELS = AVAILABLE_MODELS;
window.MODEL_CAPABILITIES = MODEL_CAPABILITIES;
window.CAPABILITY_LABELS = CAPABILITY_LABELS;
window.EXPORT_VERSION = EXPORT_VERSION;

// --- Views ---
import { loadAdmin, unloadAdmin } from './views/admin-bridge.js';
import {
    loadSettings, unloadSettings, saveSettings, getThinkingConfig,
    getAnalysisSettings, publishAnalysisSettings, registerSettingsSaveCommand
} from './views/settings-bridge.js';
import { loadReports, unloadReports, viewReport } from './views/reports-bridge.js';
import {
    loadEvaluatorsView, loadEvaluators, loadProfiles,
    unloadEvaluators, closeCategoryEditModal, closeCategoriesModal, closeEvaluatorDrawer,
    closePreviewModal, closeProfileDialog,
    evaluatorSubtabHasUnsavedChanges, guardUnsavedEvaluatorChanges
} from './views/evaluators/react-bridge.js';
import {
    loadResults, restoreConversationFilters, setOutcomeFilter, setupConversationsChrome,
} from './views/conversations/results-bridge.js';
import { closeImportModal, recoverInterruptedImport } from './views/conversations/import-bridge.js';
import { loadClientCatalog } from './api/endpoints.js';
import { setupShell } from './shell-bridge.js';
import { loadProjects, unloadProjects } from './views/projects-bridge.js';
import { loadAutonomous, unloadAutonomous } from './views/autonomous.js';

// Expose view load functions on window for the router
window.loadAdminPanel = loadAdmin;
window.unloadAdmin = unloadAdmin;
window.loadSettings = loadSettings;
window.unloadSettings = unloadSettings;
window.saveSettings = saveSettings;
window.getThinkingConfig = getThinkingConfig;
// Read by views/conversations/pipeline.js and fed by the React Settings view,
// which pushes its live draft back through publishAnalysisSettings.
window.getAnalysisSettings = getAnalysisSettings;
window.publishAnalysisSettings = publishAnalysisSettings;
window.registerSettingsSaveCommand = registerSettingsSaveCommand;
window.loadDashboard = loadReports;
window.viewReport = viewReport;
window.unloadReports = unloadReports;
window.loadProjects = loadProjects;
window.unloadProjects = unloadProjects;
window.loadAutonomous = loadAutonomous;
window.unloadAutonomous = unloadAutonomous;
window.loadEvaluatorsView = loadEvaluatorsView;
window.loadEvaluators = loadEvaluators;
window.unloadEvaluators = unloadEvaluators;
window.loadProfiles = loadProfiles;
window.closeCategoryEditModal = closeCategoryEditModal;
window.closeCategoriesModal = closeCategoriesModal;
window.closeEvaluatorDrawer = closeEvaluatorDrawer;
window.closePreviewModal = closePreviewModal;
window.closeProfileDialog = closeProfileDialog;
window.evaluatorSubtabHasUnsavedChanges = evaluatorSubtabHasUnsavedChanges;
window.guardUnsavedEvaluatorChanges = guardUnsavedEvaluatorChanges;
window.loadResults = loadResults;
window.restoreConversationFilters = restoreConversationFilters;
window.setOutcomeFilter = setOutcomeFilter;
window.closeImportModal = closeImportModal;
window.loadFirestoreClients = loadClientCatalog;

// Start loading the React bundle now: it publishes the conversation drawer's
// window contract (fetchAndOpenDetail, closeDrawer, ...) and mounts the
// command palette and the shell's chrome (confirm dialog, toaster, nudges).
// The router waits for them below, so a deep link finds the contract and the
// first view finds the dialogs registered. Module scripts run after HTML
// parsing, so static DOM elements are available.
const conversationsChromeReady = setupConversationsChrome();
const shellReady = setupShell();

// --- Router ---
import { initRouter } from './router.js';

// Bootstrap the app after auth is ready.
// DOMContentLoaded fires after module scripts execute, so all view inits are done.
document.addEventListener('DOMContentLoaded', async () => {
    await window.intelligenceAuth.ready;
    try {
        // The one probe a page load owes the backend: whether an import
        // outlived the previous page and is still running. The results table
        // is NOT loaded here; it mounts when Conversations is first opened.
        void recoverInterruptedImport();
        // Warm the shared client-name catalog (results table, dashboard).
        // Fire-and-forget: it never rejects, and a failed load is retried by
        // the next consumer because the loaded flag does not latch on error.
        void loadClientCatalog();
        await Promise.all([conversationsChromeReady, shellReady]);
        await initRouter();
    } catch (e) {
        console.error('Initialization error:', e);
    }
});
